<template>
  <div
    class="tw:absolute tw:inset-0 tw:flex tw:h-full tw:items-end tw:overflow-hidden tw:bg-gradient-to-b tw:from-transparent tw:from-50% tw:via-transparent/50 tw:via-85% tw:to-black tw:p-2 tw:opacity-0 tw:transition-opacity tw:duration-300 tw:hover:opacity-100 tw:group"
    :class="{
      'tw:bg-black/50': firstPlay,
      'tw:opacity-100': !isPlaying || playerIsBeingScrubbed,
      'tw:cursor-pointer': canPlay,
    }"
    @click="handleWrapperClick"
  >
    <BaseButton
      v-if="firstPlay"
      :aria-label="getLocaleText('inline_player.play')"
      class="tw:absolute tw:left-1/2 tw:top-1/2 tw:z-10 tw:flex tw:-translate-x-1/2 tw:-translate-y-1/2 tw:transform tw:items-center tw:justify-center tw:opacity-100"
      color="white"
      data-testid="first-play-button"
      flat
      icon="ion-ios-play"
      icon-size="54px"
      @click="$emit('toggle-play', $event)"
    />
    <div class="tw:flex tw:w-full">
      <BaseButton
        :aria-label="getLocaleValue('inline_player.rewind')"
        :disabled="!canSkipBackward"
        icon="replay_30"
        v-bind="buttonAttributes('rewind')"
      />
      <BaseButton
        :aria-label="getLocaleValue('inline_player.play')"
        :disabled="!canPlay"
        :icon="isPlaying ? 'ion-ios-pause' : 'ion-ios-play'"
        v-bind="buttonAttributes('toggle-play')"
      />
      <BaseButton
        :aria-label="getLocaleValue('inline_player.forward')"
        :disabled="!canSkipForward"
        icon="forward_30"
        v-bind="buttonAttributes('fast-forward')"
      />
      <div class="tw:-mt-1 tw:flex-1 tw:px-2">
        <slot name="slider" />
      </div>
      <BaseButton
        v-if="canShare"
        :aria-label="getLocaleValue('inline_player.share_media_item')"
        icon="ion-md-share"
        v-bind="buttonAttributes('share-media-item')"
      />
      <BaseButton
        v-if="canRenderRequestMediaButton"
        :aria-label="getLocaleValue('inline_player.open_request_media_item')"
        icon="ion-md-download"
        v-bind="buttonAttributes('request-media-item')"
      >
        <StreemTooltip>{{
          getLocaleValue("inline_player.request_media_item")
        }}</StreemTooltip>
      </BaseButton>
      <BaseButton
        v-if="canRenderMobileBackButton"
        :aria-label="getLocaleValue('inline_player.go_back')"
        icon="ion-md-contract"
        v-bind="buttonAttributes('go-back')"
      />
      <BaseButton
        v-if="downloadLink"
        :aria-label="getLocaleValue('global.download')"
        icon="ion-md-download"
        v-bind="buttonAttributes('open-download-link')"
      />
      <BaseButton
        v-if="!firstPlay && allowFullscreen"
        :aria-label="getLocaleValue('inline_player.full_screen')"
        icon="ion-md-expand"
        v-bind="buttonAttributes('full-screen')"
      />
      <BaseButton
        v-if="canRequestMediaItem"
        :aria-label="getLocaleValue('inline_player.request_media_item')"
        class="tw:mx-2 tw:text-primary"
        icon="ion-md-download"
        v-bind="buttonAttributes('request-media-item')"
      />
      <QSelect
        v-if="isAdminMode"
        ref="playbackRateSelect"
        v-model="playBackValue"
        bg-color="white"
        class="tw:scale-75 tw:bg-transparent! tw:p-0!"
        :clearable="false"
        dense
        emit-value
        map-options
        option-label="label"
        option-value="value"
        :options="playbackRateOptions"
        outlined
        rounded
        :use-input="false"
        @update:model-value="
          (val: number) => $emit('update:playback-rate', val)
        "
      />
    </div>
  </div>
</template>

<script lang="ts">
import type { Nullable } from "shared/types";
import type { RadioClip, TvClip } from "shared/types/mentions";

type Emits = {
  (e: "update:playback-rate", value: number): void;
  (e: "fast-forward", event: MouseEvent): void;
  (e: "full-screen", event: MouseEvent): void;
  (e: "go-back", event: MouseEvent): void;
  (e: "open-download-link", event: MouseEvent): void;
  (e: "request-media-item", event: MouseEvent): void;
  (e: "rewind", event: MouseEvent): void;
  (e: "share-media-item", clip: RadioClip | TvClip): void;
  (e: "toggle-play", event: MouseEvent): void;
};

// @see  https://github.com/vuejs/core/issues/8466
// Because of the way defineEmits uses overloads - it's impossible currently to get keys
type EventNames =
  | "fast-forward"
  | "full-screen"
  | "go-back"
  | "open-download-link"
  | "request-media-item"
  | "rewind"
  | "share-media-item"
  | "toggle-play";

export type InlinePlayerControlOverlayProps = {
  allowFullscreen: boolean;
  canPlay: boolean;
  canRenderMobileBackButton: boolean;
  canRenderRequestMediaButton: boolean;
  canRequestMediaItem: boolean;
  canShare: boolean;
  canSkipBackward: boolean;
  canSkipForward: boolean;
  clip: TvClip | RadioClip;
  downloadLink: string;
  firstPlay: boolean;
  isAdminMode: boolean;
  isPlaying: boolean;
  orientation: "portrait" | "landscape";
  playbackRate: number;
  playbackRateOptions: {
    label: string;
    value: number;
  }[];
};
</script>

<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { QSelect } from "quasar";
import { computed, ref } from "vue";

import { getLocaleText, getLocaleValue } from "shared/boot/i18n";
import { BaseButton, StreemTooltip } from "shared/components/base";
import useUniversalPlayerStore from "shared/stores/universalPlayer";

const universalPlayerStore = useUniversalPlayerStore();
const { playerIsBeingScrubbed } = storeToRefs(universalPlayerStore);

const props = withDefaults(defineProps<InlinePlayerControlOverlayProps>(), {
  downloadLink: "",
});

// @see https://github.com/vuejs/core/issues/8466
const emit = defineEmits<Emits>();

const playbackRateSelect = ref<Nullable<QSelect>>(null);

const playBackValue = computed({
  get: () => props.playbackRate,
  set: (value) => emit("update:playback-rate", value),
});

const buttonAttributes = computed(
  () => (eventName: Omit<EventNames, "update:playback-rate">) => {
    const onClick = (event: MouseEvent) => {
      event.stopPropagation();

      if (eventName === "request-media-item") {
        // @ts-expect-error emits wrongly typed
        emit(eventName, props.clip);
      }

      emit(eventName as Parameters<Emits>[0], event);
    };

    return {
      class: "tw:p-2",
      color: "white",
      flat: true,
      iconSize: "28px",
      onClick,
      padding: "",
    };
  }
);

const handleWrapperClick = (event: MouseEvent) => {
  event.preventDefault();

  if (props.canPlay) {
    emit("toggle-play", event);
  }
};
</script>
